import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { Newsletter } from '@/types';
import { searchNewsletters, type NewsletterSearchMatch } from '@/utils/newsletterSearch';
import { splitHighlightSegments } from '@/utils/textHighlight';
import { useDebounce } from '@/hooks/useDebounce';
import { ROUTES } from '@/constants/routes';
import { NewsletterStatusChip } from '@/components/common/Common';

const DEBOUNCE_MS = 200;

function renderHighlighted(text: string, query: string): ReactNode {
  return splitHighlightSegments(text, query).map((segment, i) =>
    segment.matched ? (
      <Box
        key={i}
        component="mark"
        sx={{ bgcolor: 'primary.light', color: 'text.primary', fontWeight: 700, px: 0.75, py: 0.15, borderRadius: 0.5 }}
      >
        {segment.text}
      </Box>
    ) : (
      <span key={i}>{segment.text}</span>
    ),
  );
}

export default function NewsletterSearchBox({ newsletters }: { newsletters: Newsletter[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  const results = useMemo(() => searchNewsletters(newsletters, debouncedQuery), [newsletters, debouncedQuery]);
  const isQueryActive = debouncedQuery.trim().length > 0;

  const goToResult = (match: NewsletterSearchMatch) => {
    navigate(ROUTES.newsletterPreview(match.newsletter.id));
    setQuery('');
  };

  return (
    // clearOnBlur defaults to true without freeSolo — intentional: clicking away from an
    // unselected query resets this nav-and-reset search box to a clean slate.
    <Autocomplete<NewsletterSearchMatch, false, false, false>
      options={results}
      open={isQueryActive}
      filterOptions={(x) => x}
      inputValue={query}
      onInputChange={(_, value) => setQuery(value)}
      value={null}
      onChange={(_, selected) => {
        if (selected) goToResult(selected);
      }}
      getOptionLabel={(option) => option.newsletter.title}
      noOptionsText={`No newsletters match "${debouncedQuery}"`}
      sx={{ width: { xs: '100%', sm: 640 } }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  #{option.newsletter.issueNumber}{' '}
                  {option.matchedField === 'title'
                    ? renderHighlighted(option.newsletter.title, debouncedQuery)
                    : option.newsletter.title}
                </Typography>
                <NewsletterStatusChip status={option.newsletter.status} />
              </Box>
              {option.snippet && (
                <Typography variant="caption" color="text.secondary">
                  Matched in {option.sectionLabel}: "{renderHighlighted(option.snippet, debouncedQuery)}"
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search by title, issue #, or content…"
          size="medium"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5, mr: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
